import {
  AutoComplete,
  FormContainer,
  ISelectType,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { Grid, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { memo, useCallback, useMemo } from "react";
import MainApi from "../../../../../../services/mainApi.service";
import {
  createESignRuleService,
  getESignRuleServiceById,
  updateESignRuleServiceById,
} from "../../../../../../services/esignRule.service";
import {
  interoperabilitySchema,
  TInteroperabilityForm,
} from "../esignRuleSchema";
import { listMainGenerics } from "../../../../../../services/generic.service";

const translationPath = "4uESign.page.automations.automationRules.";
const SERVICE_TYPE_ROUTE = process.env.REACT_APP_MAIN + "/api/v1/IService/";
const serviceRoute = new MainApi<ISelectType>(SERVICE_TYPE_ROUTE);

interface IProps {
  id?: string;
  ruleId: string;
  onBack: () => void;
}

export const InteroperabilityTab = memo(({ id, ruleId, onBack }: IProps) => {
  const { t } = useTranslation();

  const { sendRequest: save, loading: sLoading } = useFetch(
    createESignRuleService,
  );
  const { sendRequest: update, loading: uLoading } = useFetch(
    updateESignRuleServiceById,
  );
  const { sendRequest: get, loading: gLoading } = useFetch(
    getESignRuleServiceById,
  );

  const saveHandler = (v: TInteroperabilityForm) =>
    save({
      esignRuleId: ruleId,
      serviceId: v.service.id,
      directionId: v.direction.id,
    });

  const updateHandler = (v: TInteroperabilityForm, id: string) =>
    update({
      item: {
        id: id,
        serviceId: v.service.id,
        directionId: v.direction.id,
      },
      id: id,
    });

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TInteroperabilityForm>> => {
      const { data, success } = await get(id);
      if (data && success) {
        const formData: TInteroperabilityForm = {
          id: data.id,
          service: data.service,
          direction: data.direction,
        };
        return { data: formData, success: true };
      }
      return { data, success };
    },
    [get],
  );

  const directionTypeParams = useMemo(
    () => ({ Identifier: "EsignIntegrationDirectionType" }),
    [],
  );

  const loading = sLoading || uLoading || gLoading;

  return (
    <FormContainer<TInteroperabilityForm>
      saveFunction={saveHandler}
      updateFunction={updateHandler}
      getFunction={getHandler}
      loading={loading}
      title={t(translationPath + "interoperability")}
      schema={interoperabilitySchema}
      isModalForm={true}
      subtitle={
        id
          ? t(translationPath + "edit_service")
          : t(translationPath + "new_service")
      }
      backButtonOnClick={onBack}
      redirectOnSave={false}
      onSuccess={onBack}
      propId={id}
      formActionsOnBottom={false}
      fields={() => (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <AutoComplete
              name="service"
              label={t(translationPath + "service")}
              getOptionLabel={(option) => option.name}
              size="small"
              fullWidth
              request={serviceRoute.list}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <AutoComplete
              name="direction"
              label={t(translationPath + "direction")}
              getOptionLabel={(option) => option.name}
              size="small"
              fullWidth
              request={listMainGenerics}
              params={directionTypeParams}
            />
          </Grid>
        </Grid>
      )}
    />
  );
});
